/**
 * DesignProgressPanel — animação de progresso da geração do Design Studio, POR FASE.
 *
 * Defeito visto na produção (Picky V8, 2026-09-24): a barra marcava "concluído" enquanto a
 * correção de cores fora do Design System ainda rodava. Agora o backend publica a fase
 * (planejando → propondo o DS → gerando telas → corrigindo cores → gravando → concluído),
 * o X/N de cada fase, o início, o ritmo médio e a estimativa do tempo restante, além do
 * resumo do Design System proposto pela IA. Aqui:
 *   - "Concluído" só aparece quando fase === 'concluido';
 *   - cada fase mostra o seu X de N ("telas geradas 12 de 86", "telas corrigidas 3 de 9");
 *   - ritmo ("~14 s por tela") e estimativa ("faltam ~3 min") vêm do servidor;
 *   - a proposta de DS aparece com as cores (hex) e os avisos do validador.
 *
 * `PrototipoCobertura` mostra quais telas ficaram fora do protótipo navegável — nada some
 * em silêncio.
 *
 * a11y: região com aria-live="polite"; barra com role="progressbar" e aria-valuenow.
 */
import { C } from '@/theme/palette';

export const FASES_EM_ORDEM = [
  'planejando', 'extraindo_design_system', 'propondo_design_system', 'gerando_telas',
  'corrigindo_cores', 'gravando', 'concluido',
];

// Fases que só aparecem se aconteceram (brownfield extrai do repositório; greenfield propõe).
const FASES_OPCIONAIS = new Set(['extraindo_design_system', 'propondo_design_system']);

export const FASE_LABEL = {
  planejando: 'Planejando as telas do escopo',
  extraindo_design_system: 'Extraindo o Design System do repositório',
  propondo_design_system: 'Propondo o Design System pelo domínio do cliente',
  gerando_telas: 'Gerando as telas',
  corrigindo_cores: 'Corrigindo cores fora do Design System',
  gravando: 'Gravando as telas',
  concluido: 'Concluído',
  erro: 'A geração falhou',
};

const FASE_CURTA = {
  planejando: 'Planejamento',
  extraindo_design_system: 'Design System',
  propondo_design_system: 'Design System',
  gerando_telas: 'Telas',
  corrigindo_cores: 'Cores',
  gravando: 'Gravação',
  concluido: 'Concluído',
};

// Texto do contador X de N por fase (null = fase sem contador).
const FASE_CONTADOR = {
  gerando_telas: 'telas geradas',
  corrigindo_cores: 'telas corrigidas',
  gravando: 'telas gravadas',
};

const PAPEL_LABEL = {
  primary: 'Primária', 'on-primary': 'Sobre a primária', bg: 'Fundo', surface: 'Superfície',
  text: 'Texto', muted: 'Texto secundário', border: 'Borda', success: 'Sucesso',
  warning: 'Atenção', danger: 'Erro', overlay: 'Sobreposição',
};

/** Segundos → "45 s", "3 min 20 s", "1 h 5 min". null/indefinido → null. */
export function formatarDuracao(segundos) {
  if (segundos === null || segundos === undefined || Number.isNaN(Number(segundos))) return null;
  const s = Math.max(0, Math.round(Number(segundos)));
  if (s < 60) return `${s} s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m} min ${r} s` : `${m} min`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * O estado publicado pertence a ESTA geração? Um estado de uma geração anterior (ainda no
 * Redis) iniciado bem antes do clique é ignorado — senão a tela mostraria "concluído" no
 * primeiro segundo. `desde` = Date.now() do clique; 60 s de folga para relógio do servidor.
 */
export function progressoAtual(progress, desde) {
  if (!progress || !progress.fase) return null;
  if (desde && progress.iniciadoEm && progress.iniciadoEm * 1000 < desde - 60000) return null;
  return progress;
}

/** "telas geradas 12 de 86" (ou null quando a fase não tem contador). */
export function contadorDaFase(p) {
  if (!p) return null;
  const rotulo = FASE_CONTADOR[p.fase];
  if (rotulo && p.fase === 'gerando_telas' && p.totalDesconhecido) {
    return `${rotulo} ${Number(p.faseDone || 0).toLocaleString('pt-BR')} (total desconhecido)`;
  }
  if (!rotulo || !(p.faseTotal > 0)) return null;
  return `${rotulo} ${Number(p.faseDone || 0).toLocaleString('pt-BR')} de ${Number(p.faseTotal).toLocaleString('pt-BR')}`;
}

/** "~14 s por tela · faltam ~3 min" / "calculando o ritmo…" / null. */
export function ritmoEEstimativa(p) {
  if (!p || p.fase === 'concluido' || p.fase === 'erro' || !FASE_CONTADOR[p.fase]) return null;
  if (p.segundosPorItem === null || p.segundosPorItem === undefined) return 'calculando o ritmo…';
  const ritmo = `~${formatarDuracao(p.segundosPorItem)} por tela`;
  const falta = p.restanteS === null || p.restanteS === undefined ? null
    : (p.restanteS < 1 ? 'terminando a fase' : `faltam ~${formatarDuracao(p.restanteS)}`);
  return falta ? `${ritmo} · ${falta}` : ritmo;
}

function Chips({ p }) {
  const vistas = new Set([...(p.fases || []), p.fase]);
  const idx = FASES_EM_ORDEM.indexOf(p.fase);
  // Mostra as fases já vistas + as que ainda virão (a proposta de DS só se aconteceu; no
  // erro, só as que chegaram a rodar).
  const fases = FASES_EM_ORDEM.filter((f) => vistas.has(f) || (idx >= 0 && FASES_EM_ORDEM.indexOf(f) > idx))
    .filter((f) => !FASES_OPCIONAIS.has(f) || vistas.has(f));
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
      {fases.map((f) => {
        const atual = f === p.fase;
        const feita = !atual && (p.fase === 'concluido' || vistas.has(f)
          || (idx >= 0 && FASES_EM_ORDEM.indexOf(f) < idx));
        return (
          <span key={f} data-fase={f} style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 999,
            border: `1px solid ${atual ? C.cyber : C.blackBorder}`,
            color: atual ? C.cyber : feita ? C.neutralDim : C.neutralMuted,
            fontWeight: atual ? 700 : 500 }}>
            {feita ? '✓ ' : atual ? '● ' : '○ '}{FASE_CURTA[f]}
          </span>
        );
      })}
    </div>
  );
}

function PropostaDS({ ds }) {
  if (!ds || !Array.isArray(ds.cores) || ds.cores.length === 0) return null;
  const avisos = ds.avisos || [];
  return (
    <div data-testid="proposta-ds" style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8,
      border: `1px solid ${C.blackBorder}`, background: C.blackElev2 }}>
      <div style={{ fontSize: 12, color: C.neutral, fontWeight: 700, marginBottom: 6 }}>
        {ds.origem === 'repository' ? 'Design System extraído do repositório' : 'Design System proposto pela IA'}
        {ds.versao ? ` · v${ds.versao}` : ''}
        {ds.fonte ? <span style={{ fontWeight: 400, color: C.neutralDim }}> · fonte {ds.fonte}</span> : null}
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {ds.cores.map((c) => (
          <li key={c.papel} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5,
            color: C.neutralDim }}>
            {/* amostra pintada com o VALOR do token (dado do cliente, não paleta da Nava) */}
            <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: 4,
              background: c.hex, border: `1px solid ${C.blackBorder}` }} />
            <span>{PAPEL_LABEL[c.papel] || c.papel}</span>
            <code style={{ color: C.neutral }}>{c.hex}</code>
          </li>
        ))}
      </ul>
      {avisos.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 11.5, color: C.warn }}>
          {avisos.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * @param {{ progress: object|null|undefined, desde?: number }} props
 */
export function DesignProgressPanel({ progress, desde }) {
  const p = progressoAtual(progress, desde);
  if (!p) {
    return (
      <div role="status" aria-live="polite" style={{ fontSize: 12.5, color: C.neutralDim }}>
        Preparando a geração…
      </div>
    );
  }
  const contador = contadorDaFase(p);
  const ritmo = ritmoEEstimativa(p);
  const semTotal = p.fase === 'gerando_telas' && p.totalDesconhecido;
  const pct = p.faseTotal > 0 && !semTotal ? Math.min(100, Math.round((p.faseDone / p.faseTotal) * 100)) : null;
  const decorrido = p.agora && p.iniciadoEm ? formatarDuracao(p.agora - p.iniciadoEm) : null;
  const concluido = p.fase === 'concluido';
  const erro = p.fase === 'erro';
  return (
    <div role="status" aria-live="polite" data-testid="design-progresso" style={{ fontSize: 12.5, color: C.neutralDim }}>
      <Chips p={p} />
      <div style={{ color: erro ? C.hot : concluido ? C.cyber : C.neutral, fontWeight: 700 }}>
        {concluido ? '✓ Concluído' : (FASE_LABEL[p.fase] || p.fase)}
        {contador && <span style={{ marginLeft: 8, color: C.cyber }}>{contador}</span>}
      </div>
      {erro && p.erro && <div style={{ color: C.hot, marginTop: 4 }}>{p.erro}</div>}
      {pct !== null && !concluido && !erro && (
        <div role="progressbar" aria-label={FASE_LABEL[p.fase]} aria-valuemin={0}
          aria-valuemax={p.faseTotal} aria-valuenow={p.faseDone}
          style={{ marginTop: 7, height: 6, borderRadius: 999, background: C.blackBorder, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: C.cyber, transition: 'width 400ms ease' }} />
        </div>
      )}
      <div style={{ marginTop: 6, color: C.neutralMuted }}>
        {[
          ritmo,
          p.fase !== 'gerando_telas' && p.total > 0 && !p.totalDesconhecido
            ? `telas geradas ${Number(p.done).toLocaleString('pt-BR')} de ${Number(p.total).toLocaleString('pt-BR')}` : null,
          decorrido ? `em andamento há ${decorrido}` : null,
        ].filter(Boolean).join(' · ')}
      </div>
      <AvisosDaGeracao avisos={p.avisos} />
      <PropostaDS ds={p.designSystem} />
    </div>
  );
}

/**
 * Avisos da geração (teto de salvaguarda atingido, falha da IA no meio, planejamento que
 * falhou): tudo o que interrompeu a cobertura aparece — nunca só no log do servidor.
 * @param {{ avisos: string[]|undefined }} props
 */
export function AvisosDaGeracao({ avisos }) {
  const lista = (avisos || []).filter(Boolean);
  if (!lista.length) return null;
  return (
    <ul role="alert" data-testid="avisos-geracao" style={{ margin: '8px 0 0', padding: '8px 12px 8px 26px',
      borderRadius: 8, border: `1px solid ${C.warn}66`, color: C.warn, fontSize: 12, lineHeight: 1.5 }}>
      {lista.map((a) => <li key={a}>{a}</li>)}
    </ul>
  );
}

/**
 * Cobertura do protótipo navegável: telas que ficaram de fora aparecem pelo nome.
 * @param {{ prototipo: object|null|undefined }} props
 */
export function PrototipoCobertura({ prototipo }) {
  const faltando = prototipo?.telasFaltando || [];
  const cores = prototipo?.coresForaDoDs || [];
  if (!faltando.length && !cores.length) return null;
  const total = Number(prototipo?.telasTotal || 0);
  return (
    <>
      {faltando.length > 0 && (
    <div role="alert" data-testid="prototipo-faltando" style={{ margin: '10px 0', padding: '10px 12px',
      borderRadius: 8, border: `1px solid ${C.warn}66`, color: C.neutral, fontSize: 12.5, lineHeight: 1.5 }}>
      <strong style={{ color: C.warn }}>
        {faltando.length.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')} telas ficaram fora do protótipo navegável:
      </strong>{' '}
      {faltando.join('; ')}.{' '}
      As telas continuam completas no Design Studio abaixo; gere o protótipo de novo para incluí-las.
    </div>
      )}
      {cores.length > 0 && (
        <div role="alert" data-testid="prototipo-cores-fora" style={{ margin: '10px 0', padding: '10px 12px',
          borderRadius: 8, border: `1px solid ${C.warn}66`, color: C.neutral, fontSize: 12.5, lineHeight: 1.5 }}>
          <strong style={{ color: C.warn }}>
            {cores.length.toLocaleString('pt-BR')} cor(es) no protótipo fora do Design System:
          </strong>{' '}
          {cores.map((c) => <code key={c} style={{ marginRight: 6 }}>{c}</code>)}
          <div style={{ color: C.neutralDim }}>
            Essas cores não vêm dos tokens do Design System ativo. Re-tematize ou gere o protótipo de novo.
          </div>
        </div>
      )}
    </>
  );
}
